import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Calendar, Plus, Loader2, Building2, MapPin, TrendingUp, BarChart3, Tag } from 'lucide-react';
import Swal from 'sweetalert2';
import {
  crmCompanyDetail, crmListNotes, crmCreateNote, crmUpdateNote, crmDeleteNote
} from '../../../../services/vinculationCRMService';
import CreateMeetingModal from './CreateMeetingModal';
import MeetingCard from './MeetingCard';
import MeetingDetailsModal from './MeetingDetailsModal';
import CreateNoteModal from './CreateNoteModal';
import EditNoteModal from './EditNoteModal';
import NoteDetailsModal from './NoteDetailsModal';
import TooltipHelper from '../../../../components/common/TooltipHelper';

/* ==== helpers ==== */
const parseDateToLocal = (dateString) => {
  if (!dateString) return null;
  const cleanDateString = dateString.split('T')[0];
  const parts = cleanDateString.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
};

export const onlyDate = (iso) => {
  if (!iso) return '—';
  const d = parseDateToLocal(iso);
  if (!d || Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};
const MEETING_CATEGORIES = [
  { value: 'ACERCAMIENTO', label: 'Acercamiento' },
  { value: 'VINCULACION',  label: 'Vinculación' },
  { value: 'PROYECTO',     label: 'Proyecto' },
  { value: 'OTRA',         label: 'Otra' },
];
const normCat = (v) => String(v || '').trim().toUpperCase();
const num = (v, def = 0) => (Number.isFinite(Number(v)) ? Number(v) : def);
const yearOf = (iso) => { if (!iso) return null; const d = parseDateToLocal(iso); if (!d) return null; const y = d.getFullYear(); return Number.isFinite(y) ? y : null; };
const monthOf = (iso) => { if (!iso) return null; const d = parseDateToLocal(iso); if (!d) return null; const m = d.getMonth() + 1; return Number.isFinite(m) ? m : null; };
const fmt = (n) => new Intl.NumberFormat('es-CL').format(Number.isFinite(n) ? n : 0);

/* ==== Mini bar chart ==== */
const YearMiniChart = ({ baseYear, countByYear }) => {
  const years = useMemo(() => {
    const b = baseYear || new Date().getFullYear();
    return [b - 4, b - 3, b - 2, b - 1, b];
  }, [baseYear]);

  const series = years.map(y => ({ year: y, count: num(countByYear.get(y), 0) }));
  const max = Math.max(...series.map(s => s.count), 1);

  return (
    <div className="bg-white border rounded-xl p-3">
      <div className="text-sm font-semibold text-[#09274A] mb-2">Reuniones por año</div>
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(5, minmax(0,1fr))' }}>
        {series.map(({ year, count }) => {
          const h = Math.round((count / max) * 90);
          return (
            <div key={year} className="flex flex-col items-center justify-end h-28">
              <div className="w-full h-full flex items-end">
                <div
                  className="w-full bg-[#0488AF]/85 rounded-t"
                  style={{ height: `${h}%` }}
                  title={`${year}: ${fmt(count)} reunión(es)`}
                />
              </div>
              <div className="mt-1 text-[11px] text-gray-600">{year}</div>
              <div className="text-[10px] text-gray-500">{fmt(count)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MonthlyChart = ({ baseYear, meetings }) => {
  const monthData = useMemo(() => {
    const year = baseYear || new Date().getFullYear();
    const monthCounts = new Map();
    
    // Inicializar todos los meses con 0
    for (let m = 1; m <= 12; m++) {
      monthCounts.set(m, 0);
    }
    
    // Contar reuniones por mes del año seleccionado
    meetings.forEach((m) => {
      if (!m?.date) return;
      const date = new Date(m.date);
      const meetingYear = date.getFullYear();
      if (meetingYear === year) {
        const month = date.getMonth() + 1; // 1-12
        monthCounts.set(month, (monthCounts.get(month) || 0) + 1);
      }
    });
    
    // Crear array con todos los meses
    return Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      count: monthCounts.get(i + 1) || 0,
    }));
  }, [baseYear, meetings]);

  const max = Math.max(...monthData.map(m => m.count), 1);
  const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  return (
    <div className="bg-white border rounded-xl p-3">
      <div className="text-sm font-semibold text-[#09274A] mb-3">Reuniones mensuales {baseYear || new Date().getFullYear()}</div>
      <div className="flex items-end gap-2 h-32">
        {monthData.map(({ month, count }) => {
          const h = Math.round((count / max) * 100);
          return (
            <div key={month} className="flex-1 flex flex-col items-center justify-end h-full">
              <div className="w-full h-full flex flex-col items-center justify-end relative">
                <div
                  className="w-full bg-[#0488AF]/85 rounded-t transition-all hover:bg-[#0488AF] relative flex items-center justify-center"
                  style={{ height: `${h}%` }}
                  title={`${monthNames[month - 1]}: ${fmt(count)} reunión(es)`}
                >
                  {count > 0 && (
                    <div className="text-[10px] font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">{fmt(count)}</div>
                  )}
                </div>
              </div>
              <div className="mt-2 text-[10px] text-gray-700 font-medium">{monthNames[month - 1]}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function CompanyCRMModal({ open, onClose = () => {}, companyStub, initialYear = '', initialMonth = '', initialYearFrom = '', initialMonthFrom = '', initialYearTo = '', initialMonthTo = '', initialMeetingId = null, onClearInitialMeetingId, onMeetingCreated, onMeetingUpdated, onMeetingDeleted }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [company, setCompany] = useState(companyStub || null);

  // datos
  const [meetings, setMeetings] = useState([]);
  const [minutes, setMinutes] = useState([]);
  const [notes, setNotes] = useState([]);

  // UI
  const [tab, setTab] = useState('meetings');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [cat, setCat] = useState('');
  const [openCreate, setOpenCreate] = useState(false);
  const [openCreateNote, setOpenCreateNote] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState(null);
  const [noteToView, setNoteToView] = useState(null);
  const [noteDateFrom, setNoteDateFrom] = useState('');
  const [noteDateTo, setNoteDateTo] = useState('');

  // NUEVO: estado para el modal de detalles
  const [detail, setDetail] = useState(null); // { meeting, minutes }

  const userTouchedYearRef = useRef(false);
  const initialMeetingIdProcessedRef = useRef(false);
  const userClosedDetailRef = useRef(false);
  const rutKey = useMemo(() => companyStub?.rut || company?.rut || '', [companyStub, company]);

  const computeMaxYear = (list) => {
    const current = new Date().getFullYear();
    const years = (list || []).map((m) => yearOf(m?.date)).filter(Boolean);
    return String(years.length ? Math.max(current, ...years) : current);
  };

  const load = async () => {
    if (!rutKey) return;
    setLoading(true);
    try {
      const det = await crmCompanyDetail(rutKey);
      const ms  = Array.isArray(det.meetings) ? det.meetings : [];
      const companyData = det.company || companyStub;
      // Asegurar que el logo se normalice correctamente (puede venir como logo_url del backend)
      if (companyData && !companyData.logo && companyData.logo_url) {
        companyData.logo = companyData.logo_url;
      }
      setCompany(companyData);
      setMeetings(ms);
      setMinutes(Array.isArray(det.minutes) ? det.minutes : []);
      const listNotes = det.notes ?? (await crmListNotes(rutKey));
      setNotes(Array.isArray(listNotes) ? listNotes : []);
      // Si hay initialYear del filtro, usarlo; si no, calcular el máximo
      if (!userTouchedYearRef.current) {
        if (initialYear) {
          setYear(initialYear);
        } else {
          setYear(computeMaxYear(ms));
        }
      }
      // Si hay initialMonth del filtro, usarlo
      if (initialMonth) {
        setMonth(initialMonth);
      }
    } catch {
      setMeetings([]); setMinutes([]); setNotes([]);
      if (!userTouchedYearRef.current) {
        if (initialYear) {
          setYear(initialYear);
        } else {
          setYear(String(new Date().getFullYear()));
        }
      }
      // Si hay initialMonth del filtro, usarlo
      if (initialMonth) {
        setMonth(initialMonth);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open !== false) {
      setTab('meetings');
      setCat('');
      userTouchedYearRef.current = false;
      initialMeetingIdProcessedRef.current = false; // Resetear al abrir el modal
      // Si hay initialYear, usarlo; si no, vacío para que computeMaxYear lo calcule
      setYear(initialYear || '');
      // Si hay initialMonth, usarlo; si no, vacío
      setMonth(initialMonth || '');
      setDetail(null); // Resetear detail al abrir
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rutKey, initialYear, initialMonth]);

  // Abrir automáticamente la reunión si hay initialMeetingId y las reuniones ya están cargadas (solo una vez)
  useEffect(() => {
    if (initialMeetingId && meetings.length > 0 && !loading && !detail && !initialMeetingIdProcessedRef.current && !userClosedDetailRef.current) {
      const getMeetingIdFromMinute = (d) =>
        d?.meetingId ?? d?.meeting_id ?? d?.meeting?.id ?? d?.meetingID ?? d?.meetingIdFk ?? d?.meetingUuid;
      
      const meetingToOpen = meetings.find(m => String(m.id) === String(initialMeetingId));
      if (meetingToOpen) {
        const assoc = minutes.filter((d) => String(getMeetingIdFromMinute(d)) === String(initialMeetingId));
        setDetail({ meeting: meetingToOpen, minutes: assoc });
        setTab('meetings');
        initialMeetingIdProcessedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMeetingId, meetings, minutes, loading, detail]);
  
  // Resetear los refs cuando el modal se cierra completamente
  useEffect(() => {
    if (!open) {
      initialMeetingIdProcessedRef.current = false;
      userClosedDetailRef.current = false;
    }
  }, [open]);

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const ys = new Set(meetings.map(m => yearOf(m.date)).filter(Boolean));
    ys.add(current);
    return [''].concat([...ys].sort((a,b)=>b-a).map(String));
  }, [meetings]);

  const countByYear = useMemo(() => {
    const map = new Map();
    for (const m of meetings) {
      const y = yearOf(m.date);
      if (!y) continue;
      map.set(y, (map.get(y) || 0) + 1);
    }
    return map;
  }, [meetings]);

  const baseYear = useMemo(
    () => (year ? Number(year) : new Date().getFullYear()),
    [year]
  );

  const totalHist = meetings.length;
  const countThisYear = num(countByYear.get(baseYear), 0);
  const countPrevYear = num(countByYear.get(baseYear - 1), 0);
  const diff = countThisYear - countPrevYear;

  const meetingsFiltered = useMemo(() => {
    return meetings
      .filter((m) => {
        if (!m.date) return false;
        
        const meetingDate = parseDateToLocal(m.date);
        if (!meetingDate) return false;
        
        const meetingYear = meetingDate.getFullYear();
        const meetingMonth = meetingDate.getMonth() + 1; // getMonth() es 0-indexed
        
        // Si hay rango de fechas, filtrar por rango
        if (initialYearFrom && initialMonthFrom && initialYearTo && initialMonthTo) {
          const fromYear = Number(initialYearFrom);
          const fromMonth = Number(initialMonthFrom);
          const toYear = Number(initialYearTo);
          const toMonth = Number(initialMonthTo);
          
          // Verificar si la fecha está dentro del rango
          if (meetingYear < fromYear || meetingYear > toYear) return false;
          if (meetingYear === fromYear && meetingMonth < fromMonth) return false;
          if (meetingYear === toYear && meetingMonth > toMonth) return false;
        } else {
          // Si hay año individual, filtrar por año
          const okYear = !year || (meetingYear === Number(year));
          if (!okYear) return false;
          
          // Si hay mes individual, filtrar por mes
          const okMonth = !month || !year || (meetingMonth === Number(month));
          if (!okMonth) return false;
        }
        
        // Filtrar por categoría
        const okCat = !cat || normCat(m.category) === normCat(cat);
        return okCat;
      })
      .sort((a, b) => {
        const dateA = parseDateToLocal(a.date);
        const dateB = parseDateToLocal(b.date);
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        // Ordenar por fecha de reunión (más reciente primero)
        // Si las fechas son iguales, ordenar por createdAt (más reciente primero)
        const dateDiff = dateB.getTime() - dateA.getTime();
        if (dateDiff !== 0) return dateDiff;
        // Si las fechas son iguales, usar createdAt como criterio secundario
        const createdAtA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const createdAtB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return createdAtB - createdAtA;
      });
  }, [meetings, year, month, cat, initialYearFrom, initialMonthFrom, initialYearTo, initialMonthTo]);

  const lastMeetingAt = meetings.length
    ? [...meetings].sort((a, b) => {
        const dateA = parseDateToLocal(a.date);
        const dateB = parseDateToLocal(b.date);
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB.getTime() - dateA.getTime(); // Más reciente primero
      })[0].date
    : null;

  if (open === false) return null;

  /* === helpers de sincronización con las tarjetas === */
  const getMeetingIdFromMinute = (d) =>
    d?.meetingId ?? d?.meeting_id ?? d?.meeting?.id ?? d?.meetingID ?? d?.meetingIdFk ?? d?.meetingUuid;

  const handleMeetingUpdated = async (fresh) => {
    if (!fresh) return;
    // Recargar desde el backend para asegurar que la fecha se guardó correctamente
    await load();
    // Actualizar también el estado local con la respuesta del backend
    setMeetings((prev) => prev.map((x) => (String(x.id) === String(fresh.id) ? fresh : x)));
    const ny = yearOf(fresh?.date);
    if (ny) {
      setYear(String(ny));
      userTouchedYearRef.current = false;
    }
    // reflejar en el modal abierto
    setDetail((d) => (d && String(d.meeting.id) === String(fresh.id) ? { ...d, meeting: fresh } : d));
    // Notificar al board para actualizar el contador
    onMeetingUpdated?.(fresh);
    // Disparar evento global para actualizar reuniones recientes
    window.dispatchEvent(new Event('meetingUpdated'));
  };

  const handleMeetingDeleted = (id) => {
    setMeetings((prev) => prev.filter((m) => String(m.id) !== String(id)));
    setMinutes((prev) => prev.filter((d) => String(getMeetingIdFromMinute(d)) !== String(id)));
    setDetail((d) => (d && String(d.meeting.id) === String(id) ? null : d));
    // Notificar al board para actualizar el contador
    onMeetingDeleted?.(id);
    // Disparar evento global para actualizar reuniones recientes
    window.dispatchEvent(new Event('meetingDeleted'));
  };

  const onMinutesAdded = (newItems = []) => {
    if (!Array.isArray(newItems) || newItems.length === 0) return;
    setMinutes((prev) => {
      const seen = new Set(prev.map((x) => x.id ?? x.downloadUrl ?? x.viewUrl));
      const toAdd = newItems.filter((x) => !seen.has(x.id ?? x.downloadUrl ?? x.viewUrl));
      return [...toAdd, ...prev];
    });
  };

  const onMinuteDeleted = (minuteId) => {
    setMinutes((prev) => prev.filter((d) => String(d.id) !== String(minuteId)));
    setDetail((d) => (d ? { ...d, minutes: (d.minutes || []).filter((x) => String(x.id) !== String(minuteId)) } : d));
  };

  /* ==== render ==== */
  return (
    <div className="fixed inset-0 z-50" data-test="CompanyCRMModal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 flex items-start justify-center overflow-auto">
        <div className="w-full max-w-5xl mx-auto my-8">
          <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-xl bg-white">
            {/* Header */}
            <div className="relative p-6 bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white">
              <div className="flex items-start gap-3">
                <div className="w-16 h-16 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {company?.logo || company?.logo_url ? (
                    <img 
                      src={company.logo || company.logo_url} 
                      alt={company?.trade_name || company?.name || 'Logo'} 
                      className="w-full h-full object-contain p-2"
                    />
                  ) : (
                    <Building2 className="w-6 h-6" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-lg font-semibold truncate">
                    {company?.trade_name || company?.name || 'Empresa'}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-3 text-sm text-white/90">
                    <span className="inline-flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      {company?.industry || '—'}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {(company?.region || '—')}{company?.district ? ` / ${company?.district}` : ''}
                    </span>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20" title="Cerrar">
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* Tabs */}
              <div className="mt-5 flex flex-wrap gap-2">
                {(() => {
                  // Calcular reuniones del año (seleccionado o actual)
                  const targetYear = year ? Number(year) : (baseYear || new Date().getFullYear());
                  const yearMeetingsCount = meetings.filter(m => yearOf(m.date) === targetYear).length;
                  
                  return [
                    { key: 'meetings', label: `Reuniones (${yearMeetingsCount})` },
                    { key: 'summary',  label: 'Resumen', icon: <BarChart3 className="w-4 h-4 mr-2" /> },
                    { key: 'notes',    label: `Notas (${notes.length})` },
                  ];
                })().map((t) => (
                  <button
                    key={t.key}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center ${
                      tab === t.key ? 'bg-white text-[#0b3a66]' : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                    onClick={() => setTab(t.key)}
                  >
                    {t.icon || null}{t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-8">
              {tab === 'summary' && (
                <section className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-[#0b3a66] font-semibold">Resumen de actividad</h3>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <select
                        value={year}
                        onChange={(e) => { userTouchedYearRef.current = true; setYear(e.target.value); }}
                        className="w-[180px] h-11 pl-10 pr-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none"
                        title="Año base"
                      >
                        {yearOptions.map((y, i) => (
                          <option key={i} value={y}>{y || 'Usar año actual'}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="rounded-xl border p-3 relative group">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="text-xs text-gray-600">Total histórico reuniones</div>
                          <TooltipHelper text="Número total de reuniones registradas históricamente con esta empresa, sin importar el año." />
                        </div>
                        <div className="text-2xl font-bold text-[#09274A]">{fmt(totalHist)}</div>
                      </div>
                      <div className="rounded-xl border p-3 relative group">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="text-xs text-gray-600">Reuniones en {baseYear}</div>
                          <TooltipHelper text={`Número de reuniones registradas en el año ${baseYear}. Puedes cambiar el año usando el selector arriba.`} />
                        </div>
                        <div className="text-2xl font-bold text-[#0488AF]">{fmt(countThisYear)}</div>
                      </div>
                      <div className="rounded-xl border p-3 relative group">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="text-xs text-gray-600">Vs. {baseYear - 1} reuniones</div>
                          <TooltipHelper text={`Comparación entre las reuniones de ${baseYear} y ${baseYear - 1}. Muestra la diferencia (▲ aumento, ▼ disminución).`} />
                        </div>
                        <div className={`inline-flex items-center gap-1 text-lg font-semibold ${
                          diff > 0 ? 'text-green-700' : diff < 0 ? 'text-rose-700' : 'text-gray-700'
                        }`}>
                          {diff > 0 ? '▲' : diff < 0 ? '▼' : '—'} {diff === 0 ? 'sin cambio' : Math.abs(diff)}
                        </div>
                      </div>
                      <div className="rounded-xl border p-3 relative group">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="text-xs text-gray-600">Último contacto</div>
                          <TooltipHelper text="Fecha de la última reunión registrada con esta empresa, sin importar el año." />
                        </div>
                        <div className="text-lg font-semibold text-gray-800">
                          {onlyDate(lastMeetingAt)}
                        </div>
                      </div>
                    </div>
                    <YearMiniChart baseYear={baseYear} countByYear={countByYear} />
                  </div>
                  
                  {/* Gráfico mensual */}
                  <div className="mt-4">
                    <MonthlyChart baseYear={baseYear} meetings={meetings} />
                  </div>
                </section>
              )}

              {tab === 'meetings' && (
                <section>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <h3 className="text-[#0b3a66] font-semibold">Reuniones</h3>
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                      <div className="relative flex-shrink-0">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <select
                          value={year}
                          onChange={(e) => { 
                            userTouchedYearRef.current = true; 
                            setYear(e.target.value);
                            // Resetear mes al cambiar año
                            if (!e.target.value) setMonth('');
                          }}
                          className="w-[200px] h-11 pl-10 pr-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none"
                          title="Año"
                        >
                          {yearOptions.map((y, i) => (
                            <option key={i} value={y}>{y || 'Todos los años'}</option>
                          ))}
                        </select>
                      </div>
                      {year && (
                        <div className="relative flex-shrink-0">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                          <select
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            className="w-[200px] h-11 pl-10 pr-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none"
                            title="Mes"
                          >
                            <option value="">Todos los meses</option>
                            <option value="1">Enero</option>
                            <option value="2">Febrero</option>
                            <option value="3">Marzo</option>
                            <option value="4">Abril</option>
                            <option value="5">Mayo</option>
                            <option value="6">Junio</option>
                            <option value="7">Julio</option>
                            <option value="8">Agosto</option>
                            <option value="9">Septiembre</option>
                            <option value="10">Octubre</option>
                            <option value="11">Noviembre</option>
                            <option value="12">Diciembre</option>
                          </select>
                        </div>
                      )}
                      <div className="relative flex-shrink-0">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <select
                          value={cat}
                          onChange={(e) => setCat(e.target.value)}
                          className="w-[220px] h-11 pl-10 pr-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none"
                          title="Categoría"
                        >
                          <option value="">Todas las categorías</option>
                          {MEETING_CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={() => setOpenCreate(true)}
                        className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] flex-shrink-0">
                        <Plus className="w-4 h-4" /> Agregar reunión
                      </button>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-gray-500">Cargando…</div>
                  ) : meetingsFiltered.length === 0 ? (
                    <div className="rounded-2xl border border-dashed p-6 text-center text-gray-600 bg-gray-50">
                      <div className="text-sm">No hay reuniones registradas para este socio.</div>
                      <button
                        onClick={() => setOpenCreate(true)}
                        className="mt-3 inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C]">
                        <Plus className="w-4 h-4" /> Crear la primera reunión
                      </button>
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      {meetingsFiltered.map((m, idx) => {
                        const assoc = minutes.filter(
                          (d) => String(getMeetingIdFromMinute(d)) === String(m.id)
                        );
                        return (
                          <MeetingCard
                            key={m.id}
                            meeting={m}
                            companyRut={rutKey}
                            minutes={assoc}
                            busy={busy}
                            onOpenDetails={() => setDetail({ meeting: m, minutes: assoc })}
                            onUpdated={handleMeetingUpdated}
                            onDeleted={handleMeetingDeleted}
                            onMinutesAdded={onMinutesAdded}
                            onMinuteDeleted={onMinuteDeleted}
                          />
                        );
                      })}
                    </ul>
                  )}
                </section>
              )}

              {tab === 'notes' && (
                <section>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <h3 className="text-[#0b3a66] font-semibold">Notas</h3>
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                      <div className="relative flex-shrink-0">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                          type="date"
                          value={noteDateFrom}
                          onChange={(e) => setNoteDateFrom(e.target.value)}
                          placeholder="Desde"
                          max={new Date().toISOString().split('T')[0]}
                          className="w-[180px] h-11 pl-10 pr-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none"
                          title="Fecha desde"
                        />
                      </div>
                      <div className="relative flex-shrink-0">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                          type="date"
                          value={noteDateTo}
                          onChange={(e) => setNoteDateTo(e.target.value)}
                          placeholder="Hasta"
                          max={new Date().toISOString().split('T')[0]}
                          className="w-[180px] h-11 pl-10 pr-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none"
                          title="Fecha hasta"
                        />
                      </div>
                      {(noteDateFrom || noteDateTo) && (
                        <button
                          onClick={() => {
                            setNoteDateFrom('');
                            setNoteDateTo('');
                          }}
                          className="h-11 px-3 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-sm text-gray-700 flex-shrink-0"
                          title="Limpiar filtros"
                        >
                          Limpiar
                        </button>
                      )}
                      <button
                        onClick={() => setOpenCreateNote(true)}
                        className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors flex-shrink-0"
                      >
                        <Plus className="w-4 h-4" /> Crear nota
                      </button>
                    </div>
                  </div>

                  {(() => {
                    // Filtrar notas por rango de fechas
                    let filteredNotes = notes;
                    if (noteDateFrom || noteDateTo) {
                      filteredNotes = notes.filter((n) => {
                        if (!n.date) return false; // Si no tiene fecha, no mostrar si hay filtro
                        const noteDate = new Date(n.date);
                        const fromDate = noteDateFrom ? new Date(noteDateFrom) : null;
                        const toDate = noteDateTo ? new Date(noteDateTo) : null;
                        
                        if (fromDate && toDate) {
                          return noteDate >= fromDate && noteDate <= toDate;
                        } else if (fromDate) {
                          return noteDate >= fromDate;
                        } else if (toDate) {
                          return noteDate <= toDate;
                        }
                        return true;
                      });
                    }

                    return loading ? (
                      <div className="text-gray-500">Cargando…</div>
                    ) : filteredNotes.length === 0 ? (
                      <div className="rounded-2xl border border-dashed p-6 text-center text-gray-600 bg-gray-50">
                        {notes.length === 0 ? 'No hay notas.' : 'No hay notas que coincidan con los filtros.'}
                      </div>
                    ) : (
                      <ul className="space-y-3">
                        {filteredNotes.map((n) => (
                        <li key={n.id} className="p-3 rounded-xl border bg-white hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between gap-2">
                            <div 
                              className="min-w-0 flex-1 cursor-pointer"
                              onClick={() => setNoteToView(n)}
                            >
                              <div className="text-sm font-semibold text-gray-800 truncate">{n.title || 'Sin título'}</div>
                              <div className="text-sm text-gray-700 line-clamp-2 mt-1 break-words overflow-hidden">{n.body || '—'}</div>
                              <div className="text-xs text-gray-500 mt-2">
                                {n.date ? (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {onlyDate(n.date)}
                                  </span>
                                ) : (
                                  <span>Creada: {onlyDate(n.createdAt)}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </li>
                        ))}
                      </ul>
                    );
                  })()}
                </section>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal crear reunión */}
      {openCreate && (
        <CreateMeetingModal
          rut={rutKey}
          onClose={() => setOpenCreate(false)}
          onCreated={(meeting, uploadedMinutes = []) => {
            setMeetings((prev) => [meeting, ...prev]);
            if (uploadedMinutes.length) onMinutesAdded(uploadedMinutes);
            const ny = yearOf(meeting?.date);
            if (ny) {
              setYear(String(ny));
              userTouchedYearRef.current = false;
            }
            setTab('meetings');
            // Notificar al board para actualizar el contador
            onMeetingCreated?.(meeting);
            // Disparar evento global para actualizar reuniones recientes
            window.dispatchEvent(new Event('meetingCreated'));
          }}
        />
      )}

      {/* Modal de DETALLES de reunión */}
      {detail && (
        <MeetingDetailsModal
          open={!!detail}
          rut={rutKey}
          meeting={detail.meeting}
          minutes={detail.minutes}
          onClose={() => {
            setDetail(null);
            userClosedDetailRef.current = true; // Marcar que el usuario cerró el modal manualmente
            // Limpiar el initialMeetingId cuando el usuario cierra el modal de detalles manualmente
            if (initialMeetingId && onClearInitialMeetingId) {
              onClearInitialMeetingId();
            }
          }}
          onUpdated={handleMeetingUpdated}
          onDeleted={handleMeetingDeleted}
          onMinuteAdded={(min) => {
            setMinutes((prev) => [min, ...prev]);
            setDetail((d) => (d ? { ...d, minutes: [min, ...(d.minutes || [])] } : d));
          }}
          onMinuteDeleted={onMinuteDeleted}
        />
      )}

      {/* Modal crear nota */}
      {openCreateNote && (
        <CreateNoteModal
          open={openCreateNote}
          rut={rutKey}
          onClose={() => setOpenCreateNote(false)}
          onCreated={(note) => {
            setNotes((prev) => [note, ...prev]);
          }}
        />
      )}

      {/* Modal editar nota */}
      {noteToEdit && (
        <EditNoteModal
          open={!!noteToEdit}
          note={noteToEdit}
          onClose={() => setNoteToEdit(null)}
          onUpdated={(updatedNote) => {
            setNotes((prev) => prev.map((x) => (x.id === updatedNote.id ? updatedNote : x)));
            setNoteToEdit(null);
          }}
        />
      )}

      {/* Modal detalles nota */}
      {noteToView && (
        <NoteDetailsModal
          open={!!noteToView}
          note={noteToView}
          onClose={() => setNoteToView(null)}
          onEdit={() => setNoteToEdit(noteToView)}
          onDelete={async () => {
            const ask = await Swal.fire({ 
              title: 'Eliminar nota', 
              text: `¿Estás seguro de eliminar la nota "${noteToView.title || 'sin título'}"?`,
              icon: 'warning', 
              showCancelButton: true,
              confirmButtonColor: '#0488AF',
              cancelButtonColor: '#C42251',
              confirmButtonText: 'Sí, eliminar',
              cancelButtonText: 'Cancelar'
            });
            if (!ask.isConfirmed) return;
            try {
              setBusy(true);
              await crmDeleteNote(noteToView.id);
              setNotes((prev) => prev.filter((x) => x.id !== noteToView.id));
              setNoteToView(null);
              Swal.fire('Eliminada', 'La nota ha sido eliminada', 'success');
            } catch (err) {
              Swal.fire('Error', err?.message || 'No se pudo eliminar la nota', 'error');
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}
